import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { getFilenameFromContentDisposition } from '@sona/angular-util-common';
import { TargetId } from '@sona/shared-model-common';
import { Report, ReportFileData } from '@sona/shared-reports-models';
import {
    AuditTypeId,
    ICreateSingleRescanRequestDto,
    IScan,
    IScanRequestWithScore,
    ScanCategory,
    ScanRequestId
} from '@sona/shared-scan-model-common';
import { Paginated } from 'nestjs-paginate';
import { SseClient } from 'ngx-sse-client';
import { map, Observable } from 'rxjs';

import { Issue } from '../models/issue';
import { Page } from '../models/page';
import { PaginationParams } from '../models/pagination-params';
import { ScanRequestRescanChanges } from '../models/scan-request-changes';
import { createPaginationSearchParams } from '../utils/api-utils';


@Injectable({
    providedIn: 'root'
})
export class DeepScanApi {
    private readonly _baseUrl: string = '/api/reports/deep';
    private readonly _sseClient = inject(SseClient);
    private readonly _http = inject(HttpClient);

    getCategoryReport(scanRequestId: ScanRequestId, category: ScanCategory): Observable<Report>;
    getCategoryReport(scanRequestId: null | undefined, category: ScanCategory | null | undefined): never;
    getCategoryReport(scanRequestId: ScanRequestId | null | undefined, category: null | undefined): never;
    getCategoryReport(scanRequestId: ScanRequestId | null | undefined, category: ScanCategory | null | undefined): Observable<Report> | never;
    getCategoryReport(scanRequestId: ScanRequestId | null | undefined, category: ScanCategory | null | undefined) {
        if (!scanRequestId) {
            throw new Error('`scanRequestId` is required');
        } else if (!category) {
            throw new Error('`category` is required');
        }

        return this._http.get<Report>(`${this._baseUrl}/${scanRequestId}/${category}`);
    }

    getCategoryReportFile(scanRequestId: ScanRequestId, category: ScanCategory): Observable<ReportFileData>;
    getCategoryReportFile(scanRequestId: null | undefined, category: ScanCategory | null | undefined): never;
    getCategoryReportFile(scanRequestId: ScanRequestId | null | undefined, category: null | undefined): never;
    getCategoryReportFile(scanRequestId: ScanRequestId | null | undefined, category: ScanCategory | null | undefined): Observable<ReportFileData> | never;
    getCategoryReportFile(scanRequestId: ScanRequestId | null | undefined, category: ScanCategory | null | undefined) {
        if (!scanRequestId) {
            throw new Error('`scanRequestId` is required');
        } else if (!category) {
            throw new Error('`category` is required');
        }

        const searchParams = new URLSearchParams({
            type: 'pdf'
        });

        return this._http.get(`${this._baseUrl}/${scanRequestId}/report/${category}?${searchParams.toString()}`, {responseType: 'blob', observe: 'response'}).pipe(map((response) => ({
            data: response.body,
            fileName: getFilenameFromContentDisposition(response.headers.get('Content-Disposition'))
        })));
    }

    getIssues(scanRequestId: ScanRequestId, category: ScanCategory, params?: PaginationParams): Observable<Paginated<Issue>>;
    getIssues(scanRequestId: null | undefined, category: ScanCategory | null | undefined, params?: PaginationParams): never;
    getIssues(scanRequestId: ScanRequestId | null | undefined, category: null | undefined, params?: PaginationParams): never;
    getIssues(scanRequestId: ScanRequestId | null | undefined, category: ScanCategory | null | undefined, params?: PaginationParams): Observable<Paginated<Issue>> | never;
    getIssues(scanRequestId: ScanRequestId | null | undefined, category: ScanCategory | null | undefined, params?: PaginationParams) {
        if (!scanRequestId) {
            throw new Error('`scanRequestId` is required');
        } else if (!category) {
            throw new Error('`category` is required');
        }

        const urlSearchParams = createPaginationSearchParams(params, { category });

        return this._http.get<Paginated<Issue>>(`${this._baseUrl}/${scanRequestId}/issues?${urlSearchParams.toString()}`);
    }

    getPages(scanRequestId: ScanRequestId, category: ScanCategory, params?: PaginationParams): Observable<Paginated<Page>>;
    getPages(scanRequestId: null | undefined, category: ScanCategory | null | undefined, params?: PaginationParams): never;
    getPages(scanRequestId: ScanRequestId | null | undefined, category: null | undefined, params?: PaginationParams): never;
    getPages(scanRequestId: ScanRequestId | null | undefined, category: ScanCategory | null | undefined, params?: PaginationParams): Observable<Paginated<Page>> | never;
    getPages(scanRequestId: ScanRequestId | null | undefined, category: ScanCategory | null | undefined, params?: PaginationParams) {
        if (!scanRequestId) {
            throw new Error('`scanRequestId` is required');
        } else if (!category) {
            throw new Error('`category` is required');
        }

        const urlSearchParams = createPaginationSearchParams(params, { category });

        return this._http.get<Paginated<Page>>(`${this._baseUrl}/${scanRequestId}/pages?${urlSearchParams.toString()}`);
    }

    getAffectedPages(scanRequestId: ScanRequestId, category: ScanCategory, auditTypeId: AuditTypeId, params?: PaginationParams): Observable<Paginated<IScan>>;
    getAffectedPages(scanRequestId: null | undefined, category: ScanCategory | null | undefined, auditTypeId: AuditTypeId | null | undefined, params?: PaginationParams): never;
    getAffectedPages(scanRequestId: ScanRequestId | null | undefined, category: null | undefined, auditTypeId: AuditTypeId | null | undefined, params?: PaginationParams): never;
    getAffectedPages(scanRequestId: ScanRequestId | null | undefined, category: ScanCategory | null | undefined, auditTypeId: null | undefined, params?: PaginationParams): never;
    getAffectedPages(scanRequestId: ScanRequestId | null | undefined, category: ScanCategory | null | undefined, auditTypeId: AuditTypeId | null | undefined, params?: PaginationParams): Observable<Paginated<IScan>> | never;
    getAffectedPages(scanRequestId: ScanRequestId | null | undefined, category: ScanCategory | null | undefined, auditTypeId: AuditTypeId | null | undefined, params?: PaginationParams) {
        if (!scanRequestId) {
            throw new Error('`scanRequestId` is required');
        } else if (!category) {
            throw new Error('`category` is required');
        } else if (!auditTypeId) {
            throw new Error('`auditTypeId` is required');
        }

        const urlSearchParams = createPaginationSearchParams(params, { category });

        return this._http.get<Paginated<IScan>>(`${this._baseUrl}/${scanRequestId}/issues/${auditTypeId}/pages?${urlSearchParams.toString()}`);
    }

    getReportRescanChanges(scanRequestId: ScanRequestId): Observable<ScanRequestRescanChanges>;
    getReportRescanChanges(scanRequestId: null | undefined): never;
    getReportRescanChanges(scanRequestId: ScanRequestId | null | undefined): Observable<ScanRequestRescanChanges> | never;
    getReportRescanChanges(scanRequestId: ScanRequestId | null | undefined) {
        if (!scanRequestId) {
            return;
        }

        return this._sseClient.stream(`${this._baseUrl}/${scanRequestId}/changes/rescans`, {
            keepAlive: true,
            reconnectionDelay: 1000
        }, {}, 'GET').pipe(
            map((event) => JSON.parse((event as MessageEvent<string>).data) as ScanRequestRescanChanges)
        );
    }

    abort(scanRequestId: ScanRequestId) {
        return this._http.post(`${this._baseUrl}/${scanRequestId}/abort`, {});
    }

    rescanPage(deepScanRequestId: ScanRequestId, body: ICreateSingleRescanRequestDto) {
        return this._http.post<IScanRequestWithScore>(`/api/scan/request/${deepScanRequestId}/rescan`, body);
    }
}
